/**
 * Client-side visual preview of a personalized cover letter (Classic / Modern).
 * This is an on-screen approximation for the editor — the print-ready PDF is
 * rendered server-side at pay time. The merged greeting always falls back so
 * it never shows "Hi ,".
 *
 * The letter is drawn on a fixed 408px design canvas (interior offsets are
 * calibrated to it) and CSS-scaled to the `width` prop so it can fill the
 * preview pane without distorting the US-Letter aspect ratio (0.7727).
 */
const COLORS = {
  bg: '#d9d6d1',
  maroon: '#8a4636',
  brown: '#6e5b4e',
  tan: '#c8a784',
  terra: '#b5664f',
};

const bandStyle = {
  background: `repeating-linear-gradient(115deg, ${COLORS.brown} 0 38px, ${COLORS.tan} 38px 60px, ${COLORS.terra} 60px 84px, #cfcac4 84px 96px)`,
};

const BASE_W = 408; // px design canvas; all interior offsets are calibrated to this width

const toParagraphs = (body) =>
  String(body || '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

const greetingName = (leadName) => (leadName && leadName.trim()) || 'there';

const Signature = ({ branding, light }) => (
  <div
    className="flex items-center gap-3 rounded border px-3 py-2"
    style={{
      borderColor: light ? 'transparent' : COLORS.terra,
      background: light ? 'transparent' : 'rgba(255,255,255,.5)',
    }}
  >
    {branding.headshotUrl && (
      <img
        src={branding.headshotUrl}
        alt=""
        className="h-12 w-12 flex-none rounded object-cover"
      />
    )}
    <div className={light ? 'text-white' : ''}>
      <div className="font-serif text-base font-bold" style={!light ? { color: COLORS.maroon } : undefined}>
        {branding.agentName}
      </div>
      {branding.companyName && (
        <div className="text-[11px] font-bold">{branding.companyName}</div>
      )}
      <div className="mt-1 text-[10px] opacity-90">
        {branding.phone ? `${branding.phone} · ` : ''}
        {branding.email}
      </div>
    </div>
  </div>
);

// Any-aspect logo letterboxed inside a fixed, clipped box (never overflows).
const BrokerLogo = ({ url, className }) =>
  url ? (
    <div className={`${className} overflow-hidden`} style={{ border: `2px solid ${COLORS.terra}` }}>
      <img src={url} alt="" className="h-full w-full object-contain" />
    </div>
  ) : null;

const CoverLetterPreview = ({
  templateKey = 'classic',
  body,
  branding = {},
  leadName,
  width = BASE_W,
}) => {
  const paragraphs = toParagraphs(body);
  const scale = width / BASE_W;
  // Reserve the scaled footprint (keeps the 0.7727 ratio) and scale the canvas.
  // `overflow: hidden` is REQUIRED: the inner canvas keeps its un-scaled 408px
  // layout box (CSS transforms don't shrink layout), so without clipping it
  // grows the aspect-ratio box at small widths — turning a thumbnail into a
  // tall empty strip. Clipping makes the aspect-ratio height authoritative.
  const ScaleWrap = ({ children }) => (
    <div style={{ width, aspectRatio: '0.7727', overflow: 'hidden' }}>
      <div style={{ width: BASE_W, transformOrigin: 'top left', transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
  const greet = (
    <>
      Hi <span className="rounded bg-yellow-100 px-1 font-semibold text-yellow-800">{greetingName(leadName)}</span>
      , I&apos;m {branding.agentName} —
    </>
  );

  if (templateKey === 'modern') {
    return (
      <ScaleWrap>
        <div
          className="relative overflow-hidden rounded-lg bg-white text-[9px] leading-relaxed text-neutral-700 shadow-md"
          style={{ width: BASE_W, aspectRatio: '0.7727' }}
          data-testid="cover-letter-preview-modern"
        >
          <div className="absolute inset-y-0 left-0" style={{ width: '35%', background: `linear-gradient(165deg, ${COLORS.maroon}, ${COLORS.terra})` }}>
            <BrokerLogo url={branding.brokerLogoUrl} className="absolute left-3 top-5 grid h-12 w-24 place-items-center rounded bg-white p-1" />
            <div className="absolute inset-x-3 bottom-6 text-white">
              {branding.headshotUrl && (
                <img src={branding.headshotUrl} alt="" className="mb-2 h-14 w-14 rounded-full border-2 border-white object-cover" />
              )}
              <div className="font-serif text-[13px] font-bold">{branding.agentName}</div>
              {branding.companyName && <div className="text-[8px] opacity-90">{branding.companyName}</div>}
              <div className="mt-1 text-[7px] opacity-90">
                {branding.phone && <div>{branding.phone}</div>}
                {branding.email}
              </div>
            </div>
          </div>
          <div className="absolute bottom-6 right-6 top-8" style={{ left: '41%' }}>
            <div className="mb-2 text-[11px] font-bold" style={{ color: COLORS.maroon }}>{greet}</div>
            {paragraphs.map((p, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={`p-${i}`} className="mb-1.5">{p}</p>
            ))}
            <p className="mt-2">Best regards,<br />{branding.agentName}</p>
          </div>
        </div>
      </ScaleWrap>
    );
  }

  return (
    <ScaleWrap>
      <div
        className="relative overflow-hidden rounded-lg text-[9px] leading-relaxed text-neutral-700 shadow-md"
        style={{ width: BASE_W, aspectRatio: '0.7727', background: COLORS.bg }}
        data-testid="cover-letter-preview-classic"
      >
        <div className="absolute inset-x-0 top-0 h-14" style={bandStyle} />
        <BrokerLogo url={branding.brokerLogoUrl} className="absolute right-6 top-[60px] grid h-[46px] w-[116px] place-items-center rounded bg-white p-1.5" />
        {/* Body drops below the logo only when a broker logo is present. */}
        <div className={`absolute inset-x-[30px] bottom-[80px] ${branding.brokerLogoUrl ? 'top-[118px]' : 'top-[92px]'}`}>
          <div className="mb-2 text-[11px] font-bold">{greet}</div>
          {paragraphs.map((p, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={`p-${i}`} className="mb-1.5">{p}</p>
          ))}
          <p className="mt-1.5">Best regards,</p>
          <div className="mt-2">
            <Signature branding={branding} />
          </div>
        </div>
        <div className="absolute inset-x-[30px] bottom-[30px] text-[7px] text-neutral-500">
          Not intended to solicit any properties already listed for sale with another real estate agent.
        </div>
        <div className="absolute inset-x-0 bottom-0 h-14" style={{ ...bandStyle, transform: 'scaleX(-1)' }} />
      </div>
    </ScaleWrap>
  );
};

export default CoverLetterPreview;
